import type { Scene } from '@pz/scene';
import type { SurfacePreview } from '@pz/scene3d/surfaces';
import type { SurfaceViewer, SurfacePixels } from '@pz/scene3d/surface-viewer';
import type { Preview } from './preview.js';
import type {} from './three-entry.js';

// This import must remain type-only: no three.js in the main storefront bundle.
type Bundle = NonNullable<Window['Personalizy3D']>;
let loading: Promise<Bundle> | undefined;
function load(url: string): Promise<Bundle> {
  if (window.Personalizy3D?.createSurface) return Promise.resolve(window.Personalizy3D);
  return loading ??= new Promise<Bundle>((resolve, reject) => {
    const script = document.createElement('script');
    const timeout = window.setTimeout(() => { script.remove(); loading = undefined; reject(new Error('3D took too long to load. Please use the flat preview.')); }, 30_000);
    script.src = url; script.async = true;
    script.onload = () => { clearTimeout(timeout); if (window.Personalizy3D?.createSurface) resolve(window.Personalizy3D); else { loading = undefined; reject(new Error('Reload the page to update the 3D viewer.')); } };
    script.onerror = () => { clearTimeout(timeout); script.remove(); loading = undefined; reject(new Error('3D could not load. The flat preview is available.')); };
    document.head.appendChild(script);
  });
}

export class SurfacePreviewController {
  private viewer?: SurfaceViewer;
  private panel = document.createElement('section');
  private canvas = document.createElement('canvas');
  private button = document.createElement('button');
  private message = document.createElement('p');
  private generation = 0;
  private running = false;
  private disposed = false;
  private opening = false;
  private oldFlatDisplay = '';
  private pagehide = () => this.dispose();
  active = false;
  constructor(private options: {
    config: SurfacePreview & { modelUrl: string };
    labels?: Record<string, string>;
    host: HTMLElement;
    actions: HTMLElement;
    flatCanvas: HTMLCanvasElement;
    bundleUrl: string;
    rendererUrl: string;
    preview: Preview;
    scenes: () => Map<string, Scene>;
    onToggle: () => void;
  }) {
    this.button.type = 'button'; this.button.textContent = this.text('Open', '3D pregled'); this.button.setAttribute('aria-pressed', 'false');
    this.button.className = 'pz-surface-toggle';
    this.button.style.cssText = 'border:1px solid #bccfc2;border-radius:6px;background:#fff;padding:7px 12px;color:#335e45;cursor:pointer;font:inherit;font-size:12px';
    this.panel.hidden = true; this.panel.setAttribute('aria-label', '3D product preview');
    this.panel.style.cssText = 'width:100%;background:radial-gradient(#fff,#edf0e9);border-radius:12px;overflow:hidden';
    this.canvas.style.cssText = 'width:100%;height:400px;display:block;touch-action:none';
    this.canvas.tabIndex = 0;
    this.canvas.setAttribute('aria-label', 'Drag to rotate the product; scroll to zoom. Use the view buttons for fixed angles.');
    const views = document.createElement('div'); views.style.cssText = 'display:flex;flex-wrap:wrap;justify-content:center;gap:6px;padding:8px';
    for (const [name, angle, pitch] of [['Front', 0, 15], ['Right', 90, 15], ['Back', 180, 15], ['Left', -90, 15], ['Top', 0, 80], ['Reset', 0, 0]] as const) {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = this.text(name, ({ Front: 'Napred', Right: 'Desno', Back: 'Pozadi', Left: 'Levo', Top: 'Odozgo', Reset: 'Početni prikaz' })[name]);
      b.style.cssText = 'border:0;border-radius:6px;background:#fff;padding:7px 10px;cursor:pointer';
      b.onclick = () => { if (name === 'Reset') this.viewer?.resetView(); else this.viewer?.setView(angle, pitch); }; views.append(b);
    }
    this.message.style.cssText = 'font:11px/1.5 sans-serif;color:#697969;text-align:center;padding:0 15px 14px;margin:0';
    this.message.setAttribute('role', 'status');
    this.panel.append(this.canvas, views, this.message); options.host.append(this.panel); options.actions.append(this.button);
    this.button.onclick = () => { if (this.active) this.close(); else void this.open(); };
    window.addEventListener('pagehide', this.pagehide);
  }
  private text(key: string, fallback: string): string { return this.options.labels?.[`preview3d${key}`] || fallback; }
  private async open(): Promise<void> {
    if (this.opening || this.disposed) return;
    this.opening = true; this.button.disabled = true;
    this.button.textContent = this.text('Loading', 'Učitavanje 3D…');
    try {
      await this.options.preview.upgrade(this.options.rendererUrl);
      const bundle = await load(this.options.bundleUrl);
      if (this.disposed) return;
      if (!this.viewer) this.viewer = await bundle.createSurface({ canvas: this.canvas, modelUrl: this.options.config.modelUrl, config: this.options.config, onError: message => this.fail(message) });
      if (this.disposed) { this.viewer.dispose(); return; }
      this.active = true; this.panel.hidden = false;
      this.oldFlatDisplay = this.options.flatCanvas.style.display;
      this.options.flatCanvas.style.display = 'none';
      this.options.host.classList.add('pz-surface-active');
      this.button.textContent = this.text('Flat', 'Ravan prikaz'); this.button.setAttribute('aria-pressed', 'true');
      this.viewer.resize(); this.options.onToggle(); this.update();
    } catch (error) { this.fail((error as Error).message); }
    finally { this.opening = false; this.button.disabled = false; }
  }
  private close(): void {
    this.active = false; ++this.generation;
    this.panel.hidden = true; this.options.flatCanvas.style.display = this.oldFlatDisplay;
    this.options.host.classList.remove('pz-surface-active');
    this.button.textContent = this.text('Open', '3D pregled'); this.button.setAttribute('aria-pressed', 'false');
    this.options.onToggle();
  }
  private fail(message: string): void {
    this.close(); this.viewer?.dispose(); this.viewer = undefined;
    this.button.textContent = this.text('Error', '3D nije dostupan · pokušaj ponovo'); this.button.title = message;
  }
  update(): void {
    ++this.generation;
    if (!this.active || this.running || this.disposed) return;
    void this.draw();
  }
  private async draw(): Promise<void> {
    this.running = true;
    try {
      while (this.active && !this.disposed) {
        const generation = this.generation, scenes = this.options.scenes();
        const rendered = new Map<string, SurfacePixels>();
        this.message.textContent = this.text('Updating', 'Ažuriranje dizajna…');
        for (const surface of this.options.config.surfaces) {
          if (generation !== this.generation || !this.active || this.disposed) break;
          const scene = scenes.get(surface.sideId);
          if (!scene) throw new Error('A product print surface is missing');
          const pixels = rendered.get(surface.sideId) ?? await this.options.preview.surfacePixels(scene);
          rendered.set(surface.sideId, pixels);
          if (generation !== this.generation || !this.active || this.disposed) break;
          this.viewer?.setSurface(surface.id, pixels);
        }
        if (generation === this.generation) {
          this.message.textContent = this.text('Note', 'Približan prikaz · boje i tekstura mogu se razlikovati na gotovom proizvodu.');
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) { this.fail((error as Error).message); }
    finally { this.running = false; }
  }
  dispose(): void {
    if (this.disposed) return;
    this.active = false; this.disposed = true; ++this.generation;
    this.options.flatCanvas.style.display = this.oldFlatDisplay; this.options.host.classList.remove('pz-surface-active');
    this.viewer?.dispose();
    this.panel.remove(); this.button.remove(); window.removeEventListener('pagehide', this.pagehide);
  }
}
